import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Horizontal snap-scroll slider with arrow controls that works on both mobile & desktop.
 * - Native touch swipe on mobile via `scroll-snap`
 * - Left/Right arrow buttons scroll by ~80% of the visible width
 * - Optional autoPlay (paused on hover / when tab hidden)
 * - `testid` prefix used to expose data-testids for the arrows
 */
export default function Slider({
  children,
  autoPlay = true,
  interval = 4500,
  testid = "slider",
  itemClassName = "snap-start shrink-0",
  className = "",
  ariaLabel = "carousel",
}) {
  const trackRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const pausedRef = useRef(false);
  const items = React.Children.toArray(children);

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  const scrollByPage = useCallback((dir) => {
    const el = trackRef.current;
    if (!el) return;
    const delta = Math.max(el.clientWidth * 0.85, 220);
    // Wrap to start when we hit the end
    if (dir === 1 && el.scrollLeft + el.clientWidth >= el.scrollWidth - 4) {
      el.scrollTo({ left: 0, behavior: "smooth" });
    } else if (dir === -1 && el.scrollLeft <= 4) {
      el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    } else {
      el.scrollBy({ left: dir * delta, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows]);

  useEffect(() => {
    if (!autoPlay || items.length <= 1) return;
    const t = setInterval(() => {
      if (pausedRef.current || document.hidden) return;
      scrollByPage(1);
    }, interval);
    return () => clearInterval(t);
  }, [autoPlay, interval, scrollByPage, items.length]);

  return (
    <div
      className={`relative group ${className}`}
      onMouseEnter={() => (pausedRef.current = true)}
      onMouseLeave={() => (pausedRef.current = false)}
      onTouchStart={() => (pausedRef.current = true)}
      onTouchEnd={() => (pausedRef.current = false)}
      aria-label={ariaLabel}
      data-testid={testid}
    >
      <div
        ref={trackRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide scroll-smooth px-1 pb-2"
        style={{ scrollbarWidth: "none" }}
      >
        {items.map((child, i) => (
          <div key={i} className={itemClassName}>
            {child}
          </div>
        ))}
      </div>

      {/* Left arrow */}
      <button
        type="button"
        onClick={() => scrollByPage(-1)}
        aria-label="Previous slide"
        data-testid={`${testid}-prev`}
        className={`absolute top-1/2 -translate-y-1/2 -left-2 md:-left-3 z-10 w-10 h-10 md:w-11 md:h-11 rounded-full grid place-items-center shadow-lg border transition-all
          ${canLeft ? "bg-white text-[#1e1b4b] border-slate-200 hover:bg-[#ea580c] hover:text-white hover:border-[#ea580c]" : "bg-white/70 text-slate-300 border-slate-100 cursor-pointer"}`}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      {/* Right arrow */}
      <button
        type="button"
        onClick={() => scrollByPage(1)}
        aria-label="Next slide"
        data-testid={`${testid}-next`}
        className={`absolute top-1/2 -translate-y-1/2 -right-2 md:-right-3 z-10 w-10 h-10 md:w-11 md:h-11 rounded-full grid place-items-center shadow-lg border transition-all
          ${canRight ? "bg-white text-[#1e1b4b] border-slate-200 hover:bg-[#ea580c] hover:text-white hover:border-[#ea580c]" : "bg-white/70 text-slate-300 border-slate-100 cursor-pointer"}`}
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}
